import React, { useEffect, useRef, useState } from 'react';
import { Button, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { CompleteOrder, OverrideOrderArrival } from '../HelperFunctions/ShipmentFunctions';

const CompleteOrderModal = ({ isOpen, shipmentId, orderId, notify, onClose, onSuccess = null, actionType = 'complete', stopLabel = null }) => {
  const [overrideReason, setOverrideReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const reasonInputRef = useRef(null);

  const normalizedStopLabel = stopLabel || 'stop';
  const isArrivalOnlyMode = actionType === 'arrival_override';

  useEffect(() => {
    if (!isOpen) {
      setOverrideReason('');
      setErrorMessage('');
      setIsSubmitting(false);
      return;
    }

    setErrorMessage('');
    setIsSubmitting(false);

    if (reasonInputRef.current && typeof reasonInputRef.current.focus === 'function') {
      window.setTimeout(() => {
        reasonInputRef.current.focus();
      }, 0);
    }
  }, [isOpen, shipmentId, orderId, actionType, stopLabel]);

  const requireReason = () => {
    const trimmedReason = overrideReason.trim();

    if (!trimmedReason) {
      setErrorMessage('Reason is required.');
      return null;
    }

    return trimmedReason;
  };

  const handleComplete = async () => {
    if (!shipmentId || !orderId) {
      setErrorMessage('Shipment ID and Order ID are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');

      const trimmedReason = requireReason();

      if (!trimmedReason) {
        setIsSubmitting(false);
        return;
      }

      const payload = {
        arrival_override: {
          reason: trimmedReason
        },
        notes: trimmedReason
      };

      const response = await CompleteOrder(shipmentId, orderId, payload);

      if (response.status === 200) {
        notify('tc', `${normalizedStopLabel} marked completed with admin reason recorded.`);
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
        onClose();
      }
    } catch (error) {
      setErrorMessage(error.message || 'An error occurred while completing the order');
      setIsSubmitting(false);
    }
  };

  const handleArrivalOverride = async () => {
    if (!shipmentId || !orderId) {
      setErrorMessage('Shipment ID and Order ID are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');

      const trimmedReason = requireReason();

      if (!trimmedReason) {
        setIsSubmitting(false);
        return;
      }

      const response = await OverrideOrderArrival(shipmentId, orderId, {
        arrival_override: {
          reason: trimmedReason
        },
        notes: trimmedReason
      });

      if (response.status === 200) {
        notify('tc', `${normalizedStopLabel} marked arrived. Driver can continue checklist, photos, GoPilot, and signatures.`);
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
        onClose();
      }
    } catch (error) {
      setErrorMessage(error.message || 'An error occurred while overriding arrival');
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} backdrop="static">
      <ModalHeader toggle={onClose}>
        {isArrivalOnlyMode ? `Mark ${normalizedStopLabel} Arrived` : `Mark ${normalizedStopLabel} Completed`}
      </ModalHeader>
      <ModalBody>
        <p style={{ marginBottom: '12px' }}>
          {isArrivalOnlyMode
            ? `This marks ${normalizedStopLabel.toLowerCase()} as arrived without completing the stop. The driver can continue checklist, photos, GoPilot, and signatures.`
            : `This manually completes ${normalizedStopLabel.toLowerCase()} on behalf of the driver.`}
        </p>
        <FormGroup>
          <Label for="dispatch-complete-order-reason">Admin reason</Label>
          <textarea
            id="dispatch-complete-order-reason"
            className="form-control"
            rows="4"
            value={overrideReason}
            ref={reasonInputRef}
            onChange={event => {
              setOverrideReason(event.target.value);
              if (errorMessage) {
                setErrorMessage('');
              }
            }}
            placeholder={isArrivalOnlyMode
              ? 'Required. Explain why dispatch is marking this stop arrived without completing it.'
              : 'Required. Explain why dispatch is completing this stop on behalf of the driver.'}
            style={{ color: '#212529', backgroundColor: '#ffffff', caretColor: '#212529' }}
          />
        </FormGroup>
        {errorMessage ? <p style={{ color: '#dc3545', marginBottom: 0 }}>{errorMessage}</p> : null}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        {isArrivalOnlyMode ? (
          <Button color="warning" onClick={handleArrivalOverride} disabled={isSubmitting || !overrideReason.trim()}>
            Mark Arrived
          </Button>
        ) : (
          <Button color="success" onClick={handleComplete} disabled={isSubmitting || !overrideReason.trim()}>
            Mark Completed
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default CompleteOrderModal;